import React, { useCallback, useEffect, useMemo, useState } from 'react';

import axios from 'axios';
import { type Dayjs } from 'dayjs';
import _ from 'lodash';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { createContainer, useContainer } from 'unstated-next';

import {
    filterByEndDate,
    filterBySearch,
    filterByStartDate,
    sortByClassSessionName,
    sortByDates,
    sortByInstructors
} from './ClassSessionFilters/filters';
import {
    type ClassSessionWithInstructors,
    Status,
    type Statuses,
    type Option,
    type InstructorWithEmail,
    type Direction,
    type SortBy,
    SortOption
} from './types';
import { CLASSES_DATA_PORTAL } from '../../../../config/apiPaths';
import { getUrls } from '../../../../utils/url';
import AdminAuthContainer from '../../AdminAuthContainer';

const useClassSessions = () => {
    const { t } = useTranslation('portal');
    const { uuid: userUuid, authMode, showDemoContent } = useContainer(AdminAuthContainer);
    const [statuses, setStatuses] = useState<Statuses>({ fetchClassSessions: Status.INITIAL });

    const [classSessions, setClassSessions] = useState<ClassSessionWithInstructors[]>([]);
    const [instructors, setInstructors] = useState<InstructorWithEmail[]>([]);

    const [selectedInstructor, setSelectedInstructor] = useState<Option | null>(null);
    const [searchValue, setSearchValue] = useState('');

    const [startDate, setStartDate] = useState<Dayjs | null>(null);
    const [endDate, setEndDate] = useState<Dayjs | null>(null);

    const [sortDirection, setSortDirection] = useState<Direction>(undefined);
    const [sortBy, setSortBy] = useState<SortBy>(undefined);

    const { enqueueSnackbar } = useSnackbar();

    const { api } = getUrls();

    const fetchClassSessions = useCallback(
        async (uuid, authModeValue, showDemoContentValue) => {
            try {
                setStatuses((previous) => ({
                    ...previous,
                    fetchClassSessions: Status.PENDING
                }));

                const { data } = await axios.get<ClassSessionWithInstructors[]>(
                    `${api}${CLASSES_DATA_PORTAL}?user_uuid=${uuid}&auth_mode=${authModeValue}&demo_content=${showDemoContentValue}`
                );

                const instructorMap = {};

                data.forEach((classSessionWithInstructors) => {
                    classSessionWithInstructors.instructors.forEach((instructor) => {
                        if (instructor.email) {
                            instructorMap[instructor.email.toLocaleLowerCase()] = instructor;
                        }
                    });
                });

                setInstructors(Object.values(instructorMap));

                setStatuses((previous) => ({
                    ...previous,
                    fetchClassSessions: Status.SUCCESS
                }));

                setClassSessions(data);
            } catch (err) {
                setStatuses((previous) => ({
                    ...previous,
                    fetchClassSessions: Status.ERROR
                }));

                enqueueSnackbar(t('classSessions.somethingWentWrong'), { variant: 'error' });
            }
        },
        [api, enqueueSnackbar, t]
    );

    React.useEffect(() => {
        console.log('authMode changed', authMode);
        setStatuses((previous) => ({
            ...previous,
            fetchClassSessions: Status.INITIAL
        }));
    }, [authMode]);

    useEffect(() => {
        if (statuses.fetchClassSessions === Status.INITIAL && userUuid) {
            fetchClassSessions(userUuid, authMode, showDemoContent);
        }
    }, [fetchClassSessions, statuses.fetchClassSessions, userUuid, authMode, showDemoContent]);

    const filteredClassSessionsBySearch: ClassSessionWithInstructors[] = useMemo(
        () => filterBySearch({ classSessions, searchValue }),
        [classSessions, searchValue]
    );
    const filteredClassSessionsByStartDate: ClassSessionWithInstructors[] = useMemo(
        () => filterByStartDate({ classSessions: filteredClassSessionsBySearch, startDate }),
        [filteredClassSessionsBySearch, startDate]
    );
    const filteredClassSessionsByEndDate: ClassSessionWithInstructors[] = useMemo(
        () => filterByEndDate({ classSessions: filteredClassSessionsByStartDate, endDate }),
        [endDate, filteredClassSessionsByStartDate]
    );

    const clonedClassSessions = _.cloneDeep(filteredClassSessionsByEndDate);
    const sortedClassSessionsByName: ClassSessionWithInstructors[] = useMemo(
        () =>
            sortBy === SortOption.NAME
                ? sortByClassSessionName({ classSessions: clonedClassSessions, sort: sortDirection })
                : clonedClassSessions,
        [clonedClassSessions, sortBy, sortDirection]
    );
    const sortedClassSessionsByDates: ClassSessionWithInstructors[] = useMemo(
        () =>
            sortBy === SortOption.DATES
                ? sortByDates({ classSessions: sortedClassSessionsByName, sort: sortDirection })
                : sortedClassSessionsByName,
        [sortedClassSessionsByName, sortBy, sortDirection]
    );
    const filteredClassSessions: ClassSessionWithInstructors[] = useMemo(
        () =>
            sortBy === SortOption.INSTRUCTOR
                ? sortByInstructors({ classSessions: sortedClassSessionsByDates, sort: sortDirection })
                : sortedClassSessionsByDates,
        [sortedClassSessionsByDates, sortBy, sortDirection]
    );

    return {
        endDate,
        statuses,
        startDate,
        instructors,
        searchValue,
        setEndDate,
        setStartDate,
        setSearchValue,

        sortBy,
        setSortBy,
        sortDirection,
        setSortDirection,

        filteredClassSessions,
        selectedInstructor,
        setSelectedInstructor
    };
};

const ClassSessionsContainer = createContainer(useClassSessions);

export default ClassSessionsContainer;
